<script lang="ts" module>
  import { onDestroy } from 'svelte';

  import type { AssistantInstruction, SubEvaluation } from '@/teoCore/TeoApi';
  import type { AnalysisResult } from '@/teoCore/text/AnalysisResult';
  import type { TeoText } from '@/teoCore/text/TeoText';
  import DoneCard from '@/ui/components/assistant/Performance/DoneCard/DoneCard.svelte';
  import InitCard from '@/ui/components/assistant/Performance/InitCard/InitCard.svelte';
  import InstructionCard from '@/ui/components/assistant/Performance/InstructionCard/InstructionCard.svelte';
  import { useTeoTextUiStateObserver } from '@/utils/use-teo-event-observer.svelte';

  import AiComponent from './AIComponent.svelte';

  export type AssistantInstructionExtended = AssistantInstruction & {
    sub_evaluation?: SubEvaluation;
  };
</script>

<script lang="ts">
  const { teoText }: { teoText: TeoText } = $props();
  let instruction: AssistantInstructionExtended | undefined = $state();
  let assistantState: 'init' | 'instructions' | 'done' = $state('init');
  let analysisLoading = $state(false);

  const dimension = useTeoTextUiStateObserver(teoText.ui, 'dimension');
  // const textProperty = useTeoTextUiStateObserver(teoText.ui, 'textProperty');

  if (teoText.analysis) {
    onAnalysis(teoText.analysis);
  }

  teoText.events.addEventListener('gotAnalysis', onAnalysis);
  teoText.events.addEventListener('analysisPending', onAnalysisPending);
  onDestroy(() => {
    teoText.events.removeEventListener('gotAnalysis', onAnalysis);
    teoText.events.removeEventListener('analysisPending', onAnalysisPending);
  });

  $effect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    dimension.current;
    if (teoText.analysis && dimension.current) {
      assistantState = 'instructions';
      updateInstruction(teoText.analysis, dimension.current);
    } else if (teoText.analysis?.isGood()) {
      assistantState = 'done';
    }
  });

  $effect(() => {
    if (assistantState !== 'instructions' && teoText.ai.isEnabled) {
      teoText.ai.disableAiMode();
    }
  });

  function onAnalysisPending(pending: boolean) {
    analysisLoading = pending;
    if (pending) {
      assistantState = 'init';
    }
  }

  function onAnalysis(result: AnalysisResult) {
    updateInstruction(result);
    if (!result.isGood()) {
      assistantState = 'instructions';
    } else {
      teoText.ui.update('dimension', undefined);
      teoText.ui.update('textProperty', undefined);
      assistantState = 'done';
    }
    analysisLoading = false;
  }

  function updateInstruction(result: AnalysisResult, dimension?: string) {
    const instructionExt: AssistantInstructionExtended | undefined =
      result?.updateCurrentInstruction(dimension);
    if (instructionExt) {
      const indices = result.getIndices();
      if (instructionExt.index_name && indices[instructionExt.index_name]) {
        instructionExt.sub_evaluation = indices[instructionExt.index_name];
      } else {
        instructionExt.sub_evaluation = undefined;
      }
      instruction = instructionExt;
      teoText.ui.update('dimension', instructionExt.index_name);
    } else instruction = undefined;
  }
</script>

{#if assistantState === 'init'}
  <InitCard text={teoText} cardLabel="Deine Aufgabe" loading={analysisLoading}></InitCard>
{:else if assistantState === 'instructions'}
  <div class="flex flex-col space-y-4">
    <InstructionCard cardLabel="Deine Aufgabe" {instruction}></InstructionCard>
    <AiComponent {teoText}></AiComponent>
  </div>
{:else}
  <DoneCard cardLabel="Deine Aufgabe"></DoneCard>
{/if}
